import { checkOwnerName } from "@quickpoint.me/shared";
import { useEffect, useState } from "react";

export type NameAvailability = "idle" | "checking" | "available" | "taken";

const VALID_NAME = /^[a-zA-Z0-9_-]+$/;
const DEBOUNCE_MS = 400;

/**
 * Live, debounced owner-name availability check for the registration inputs.
 * Advisory only — /register is the authority, a name can still be lost
 * between check and claim. Network errors stay quiet here; the submit path
 * reports them.
 */
export function useOwnerNameAvailability(
	name: string | undefined,
): NameAvailability {
	const [status, setStatus] = useState<NameAvailability>("idle");

	useEffect(() => {
		const candidate = name?.trim().toLowerCase();
		setStatus("idle");
		if (!candidate || candidate.length < 3 || !VALID_NAME.test(candidate)) {
			return;
		}

		let cancelled = false;
		const timer = setTimeout(async () => {
			setStatus("checking");
			const res = await checkOwnerName(candidate);
			if (cancelled) return;
			if (res.networkError) {
				setStatus("idle");
				return;
			}
			setStatus(res.success ? "available" : "taken");
		}, DEBOUNCE_MS);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [name]);

	return status;
}
